// Unsaved-work protection (security policy, Phase 5). Same file in the admin app (BU),
// HRMS, kanban and PWA; works on every form without changing any screen.
//
// - Remembers what the user really changed on the current page (trusted events only):
//   text fields, text areas, native selects, checkboxes / radios, date-picker inputs,
//   dropdowns (MUI Select / Autocomplete, react-select: the option they picked),
//   rich-text editors (CKEditor, TinyMCE) — also inside a popup, together with the
//   click that opened that popup (e.g. the edit icon in a table row).
// - When the app logs the user out by itself (inactivity, 8-hour limit, session ended
//   by the server) saveDraft() snapshots it. Embedded apps (HR / Kanban iframes in BU)
//   save their own and tell BU.
// - After logging in again the app goes straight back to that page (and popup) and
//   fills everything in, then says so. Only if that fails a banner offers to retry.
//
// Stored in sessionStorage: this browser tab only, gone when the tab is closed, tied to
// the user id, kept 8 hours at most. Never passwords, files or hidden fields.

const KEY = "bu.unsavedDraft";
// Main app (BU): an embedded app (HR / Kanban iframe) kept a draft while this page
// showed it. The iframe only exists on that page, so the main app brings the user there.
const FRAME_KEY = "bu.unsavedFrameDraft";
const embedded = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};
const tellParent = (type, extra) => {
  if (!embedded()) return;
  try {
    window.parent.postMessage({ type, ...extra }, "*");
  } catch (e) {
    /* parent gone */
  }
};
const MAX_AGE_MS = 8 * 3600 * 1000;
const MAX_FIELDS = 200;
const MAX_VALUE = 20000;
const MAX_PICKS = 50;
const SKIP_TYPES = ["password", "hidden", "file", "search", "submit", "button", "reset", "image"];

let opts = null;
let dirty = new Set(); // text-like fields changed on this page
// the same fields by identity: a form that re-renders replaces its <input> elements, so
// at save time the value is read from whichever element now has that identity
let dirtyKeys = new Map();
let keysTimer = null;
let keptOpener = null; // what opened the popup a captured field was in
let picks = []; // dropdown choices made on this page, in order
let richDirty = new Set(); // contenteditable editors (CKEditor) typed in
let lastCombo = null; // dropdown the user opened last
let lastPath = null;
let banner = null;
let started = false;
const failedOpen = new Set(); // drafts that could not be filled in (the card then says so)
let busy = false; // opening the saved page + filling it in: no banner meanwhile
let askTimer = null;
let lastUserId = null; // who is logged in (kept for answers that arrive during a logout)
const autoTried = new Set(); // drafts already restored automatically once (this page load)
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ── Popups ───────────────────────────────────────────────────────────────────────
const DIALOG_SEL = '[role="dialog"], [aria-modal="true"], .modal.show, .MuiDialog-root, .ReactModal__Content';
const ROW_SEL = 'tr, [role="row"], li, [class*="card"], [class*="Card"]';
const OPENER_SEL = 'button, a, [role="button"], [role="menuitem"], [role="tab"], [role="link"]';
const dialogOf = (el) => (el && el.closest ? el.closest(DIALOG_SEL) : null);
const openDialog = () => {
  const all = [...document.querySelectorAll(DIALOG_SEL)].filter((d) => d.getClientRects().length && !(banner && banner.contains(d)));
  return all[all.length - 1] || null;
};
// the page (outside popups) or the open popup
const scopeRoot = (dlg) => (dlg ? openDialog() : document);
const inScope = (root, el) => (root === document ? !dialogOf(el) : true);
const textOf = (n) => ((n && (n.innerText || n.textContent)) || "").replace(/\s+/g, " ").trim();

// ── Dropdowns (MUI Select / Autocomplete, react-select) ─────────────────────────
// react-select controls are "css-…-control" or "<prefix>__control" (NOT Bootstrap's "form-control")
const COMBO_SEL = '[role="combobox"], [aria-haspopup="listbox"], [class*="css-"][class*="-control"]:not(.form-control), [class*="__control"]';
const OPTION_SEL = '[role="option"], [id*="-option-"]';
const outerCombo = (el) => {
  let c = el && el.closest ? el.closest(COMBO_SEL) : null;
  while (c && c.parentElement && c.parentElement.closest(COMBO_SEL)) c = c.parentElement.closest(COMBO_SEL);
  return c;
};
const isInCombo = (el) => !!(el && el.closest && el.closest(COMBO_SEL));

const isField = (el) => {
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") return false;
  if (tag === "INPUT" && SKIP_TYPES.includes(String(el.type || "text").toLowerCase())) return false;
  if (el.autocomplete === "one-time-code" || el.closest("[data-no-draft]")) return false;
  if (tag !== "SELECT" && isInCombo(el)) return false; // dropdown search boxes: the picked option is kept instead
  return true;
};

// React / MUI generate ids like ":r5:" or "mui-123" that change between renders.
const stableId = (id) => (id && !/^:r|^mui-\d|^react-select-\d/.test(id) ? id : "");
const labelOf = (el) => {
  const lab = el.labels && el.labels[0];
  return (lab ? lab.textContent : "").trim().slice(0, 60);
};
const baseSig = (el) =>
  [el.tagName, el.type || "", el.name || "", stableId(el.id), el.getAttribute("placeholder") || "", el.getAttribute("aria-label") || "", labelOf(el)].join("|");

// Signature + its position among fields with the same signature (document order).
const signatures = (root = document) => {
  const seen = {};
  const out = new Map();
  root.querySelectorAll("input, textarea, select").forEach((el) => {
    if (!isField(el)) return;
    const b = baseSig(el);
    seen[b] = (seen[b] || 0) + 1;
    out.set(el, `${b}#${seen[b]}`);
  });
  return out;
};

const comboLabel = (cb) => {
  const box = cb.closest(".MuiFormControl-root, .MuiAutocomplete-root, .MuiTextField-root, .form-group, .mb-3, .mb-2, [class*='col']");
  const lab = box && box.querySelector("label");
  const ids = String(cb.getAttribute("aria-labelledby") || "")
    .split(/\s+/)
    .filter((id) => id && id !== cb.id);
  const lb = ids.map((id) => textOf(document.getElementById(id))).join(" ");
  const ph = cb.querySelector && cb.querySelector('[class*="placeholder"]');
  return (textOf(lab) || cb.getAttribute("aria-label") || lb || cb.getAttribute("name") || "").slice(0, 60) || (ph ? "ph:" + textOf(ph).slice(0, 40) : "");
};
const combosIn = (root) => [...root.querySelectorAll(COMBO_SEL)].filter((c) => inScope(root, c) && outerCombo(c) === c);
const comboKey = (cb) => {
  const dlg = !!dialogOf(cb);
  const root = dlg ? dialogOf(cb) : document;
  const base = comboLabel(cb);
  const same = combosIn(root).filter((c) => comboLabel(c) === base);
  return { dlg, sig: `${base}#${same.indexOf(cb) + 1}` };
};
const findCombo = (k) => {
  const root = scopeRoot(k.dlg);
  if (!root) return null;
  const [base, n] = [k.sig.slice(0, k.sig.lastIndexOf("#")), Number(k.sig.slice(k.sig.lastIndexOf("#") + 1))];
  return combosIn(root).filter((c) => comboLabel(c) === base)[n - 1] || null;
};

// ── Rich-text editors ────────────────────────────────────────────────────────────
const editablesIn = (root) => [...root.querySelectorAll('[contenteditable="true"]')].filter((x) => inScope(root, x) && !x.closest("[data-no-draft]"));
const tinyEditors = () => {
  try {
    const tm = window.tinymce;
    return tm && tm.get ? tm.get() : [];
  } catch (e) {
    return [];
  }
};

// ── What opened the popup ───────────────────────────────────────────────────────
const elSig = (el) =>
  [el.tagName, el.getAttribute("aria-label") || "", el.getAttribute("title") || "", el.getAttribute("data-testid") || "", textOf(el).slice(0, 60)].join("|");
let opener = null; // last click outside a popup (what opened the popup that is open now)
const describeOpener = (el) => {
  const row = el.closest(ROW_SEL);
  const scope = row || document;
  const same = [...scope.querySelectorAll(el.tagName)].filter((x) => elSig(x) === elSig(el));
  return { sig: elSig(el), tag: el.tagName, row: row ? textOf(row).slice(0, 300) : null, rowTag: row ? row.tagName : null, idx: same.indexOf(el), path: null };
};
const findOpener = (o) => {
  let scopes = [document];
  if (o.row) scopes = [...document.querySelectorAll(ROW_SEL)].filter((r) => r.tagName === o.rowTag && textOf(r).slice(0, 300) === o.row);
  for (const sc of scopes) {
    const same = [...sc.querySelectorAll(o.tag)].filter((x) => elSig(x) === o.sig);
    if (same[o.idx]) return same[o.idx];
  }
  return null;
};

// ── Storage ─────────────────────────────────────────────────────────────────────
const read = () => {
  try {
    const d = JSON.parse(sessionStorage.getItem(KEY) || "null");
    if (!d || Date.now() - d.savedAt > MAX_AGE_MS) return null;
    d.fields = d.fields || [];
    d.picks = d.picks || [];
    d.rich = d.rich || [];
    return d;
  } catch (e) {
    return null;
  }
};
const write = (d) => {
  try {
    if (d) sessionStorage.setItem(KEY, JSON.stringify(d));
    else sessionStorage.removeItem(KEY);
  } catch (e) {
    /* storage full / blocked: nothing kept */
  }
  // inside BU: tell it every time (BU brings the user back after the next login)
  if (d) tellParent("BU_DRAFT_SAVED", { savedAt: d.savedAt });
  else tellParent("BU_DRAFT_CLEARED");
};
const readFrame = () => {
  try {
    const d = JSON.parse(sessionStorage.getItem(FRAME_KEY) || "null");
    if (!d || Date.now() - d.savedAt > MAX_AGE_MS) return null;
    return d;
  } catch (e) {
    return null;
  }
};
const writeFrame = (d) => {
  try {
    if (d) sessionStorage.setItem(FRAME_KEY, JSON.stringify(d));
    else sessionStorage.removeItem(FRAME_KEY);
  } catch (e) {
    /* storage blocked */
  }
};
// Without the one-time login `token` the admin app puts in the HR iframe URL: it must
// never be stored, and an old one opened again logs the embedded app out (white page).
const currentPath = () => {
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete("token");
    return u.pathname + u.search;
  } catch (e) {
    return window.location.pathname;
  }
};
// The router's page state (e.g. the record a list passed to its edit page).
const routerState = () => {
  try {
    const st = window.history.state;
    return st == null ? null : JSON.parse(JSON.stringify(st));
  } catch (e) {
    return null;
  }
};

// Open a page of this app WITHOUT reloading it: a reload loses the login hand-over of
// embedded apps (HRMS / Kanban inside BU) and the page's router state (white screen).
// The router follows the popstate event.
const openInApp = (path, state) => {
  try {
    window.history.pushState(state == null ? null : state, "", path);
    window.dispatchEvent(new PopStateEvent("popstate", { state: state == null ? null : state }));
  } catch (e) {
    window.location.assign(path);
  }
};

const refreshKeys = () => {
  keysTimer = null;
  const sigs = signatures();
  const dlgSigs = new Map();
  dirty.forEach((el) => {
    if (!el.isConnected) return;
    const d = dialogOf(el);
    if (d && !dlgSigs.has(d)) dlgSigs.set(d, signatures(d));
    const sig = d ? dlgSigs.get(d).get(el) : sigs.get(el);
    if (!sig) return;
    if (d && opener && opener.path === currentPath()) keptOpener = opener;
    // keep the value too: at logout the form may already be gone from the page
    const isCheck = el.type === "checkbox" || el.type === "radio";
    dirtyKeys.set(`${d ? "d" : "p"}|${sig}`, {
      dlg: !!d,
      sig,
      ...(isCheck ? { checked: !!el.checked } : { value: String(el.value == null ? "" : el.value).slice(0, MAX_VALUE) }),
    });
  });
};
const scheduleKeys = () => {
  if (!keysTimer) keysTimer = setTimeout(refreshKeys, 300);
};

// ── Save ────────────────────────────────────────────────────────────────────────
/** Snapshot what the user changed on this page. Returns the number of things saved. */
export const saveDraft = (reason) => {
  if (!opts) return 0;
  const userId = opts.getUserId();
  if (!userId) return 0;
  const open = openDialog();
  const sigs = signatures();
  const typedInPopup = [...dirty].map(dialogOf).find((d) => d && d.isConnected) || null;
  const pickedInPopup = open && (picks.some((p) => p.dlg) || [...richDirty].some((x) => dialogOf(x))) ? open : null;
  const dlg = typedInPopup || pickedInPopup;
  const dlgSigs = dlg ? signatures(dlg) : null;
  const fields = [];
  dirty.forEach((el) => {
    if (!el.isConnected || fields.length >= MAX_FIELDS) return;
    const inDlg = dlg && dlg.contains(el);
    const sig = inDlg ? dlgSigs.get(el) : dialogOf(el) ? null : sigs.get(el);
    if (!sig) return;
    const isCheck = el.type === "checkbox" || el.type === "radio";
    fields.push({
      sig,
      ...(inDlg ? { dlg: true } : {}),
      ...(isCheck ? { checked: !!el.checked } : { value: String(el.value == null ? "" : el.value).slice(0, MAX_VALUE) }),
    });
  });
  // fields whose element was replaced since the user typed: find them again by identity
  const have = new Set(fields.map((f) => `${f.dlg ? "d" : "p"}|${f.sig}`));
  const pageBySig = new Map();
  sigs.forEach((sig, el) => {
    if (!dialogOf(el)) pageBySig.set(sig, el);
  });
  const dlgBySig = new Map();
  if (dlgSigs) dlgSigs.forEach((sig, el) => dlgBySig.set(sig, el));
  dirtyKeys.forEach((k, key) => {
    if (have.has(key) || fields.length >= MAX_FIELDS) return;
    const el = k.dlg ? dlgBySig.get(k.sig) : pageBySig.get(k.sig);
    if (!el) {
      // the field is not on the page any more: use the value kept while typing
      fields.push({ ...k });
      return;
    }
    const isCheck = el.type === "checkbox" || el.type === "radio";
    fields.push({ sig: k.sig, ...(k.dlg ? { dlg: true } : {}), ...(isCheck ? { checked: !!el.checked } : { value: String(el.value == null ? "" : el.value).slice(0, MAX_VALUE) }) });
  });
  const keptPicks = picks.filter((p) => !p.dlg || dlg).slice(-MAX_PICKS);
  const rich = [];
  richDirty.forEach((el) => {
    if (!el.isConnected) return;
    const inDlg = !!dialogOf(el);
    if (inDlg && !dlg) return;
    const list = editablesIn(inDlg ? dialogOf(el) : document);
    const inst = el.ckeditorInstance;
    rich.push({ kind: "ce", dlg: inDlg, idx: list.indexOf(el), html: String(inst ? inst.getData() : el.innerHTML).slice(0, MAX_VALUE * 5) });
  });
  tinyEditors().forEach((ed, idx) => {
    try {
      if (ed.isDirty && ed.isDirty()) rich.push({ kind: "tiny", idx, html: String(ed.getContent()).slice(0, MAX_VALUE * 5) });
    } catch (e) {
      /* editor gone */
    }
  });
  const path = currentPath();
  let op = dlg && opener && opener.path === path ? opener : null;
  // Saved already during this logout (several logout paths can each save, and the form may
  // have re-rendered in between): merge, never replace a fuller save with a poorer one.
  const prev = read();
  if (prev && autoTried.has(prev.savedAt) && prev.path === path && prev.userId === String(userId)) {
    const key = (f) => `${f.dlg ? "d" : "p"}|${f.sig}`;
    const merged = new Map(prev.fields.map((f) => [key(f), f]));
    fields.forEach((f) => merged.set(key(f), f));
    fields.length = 0;
    merged.forEach((f) => fields.push(f));
    if (!keptPicks.length) keptPicks.push(...prev.picks);
    if (!rich.length) rich.push(...prev.rich);
    op = op || prev.opener || null;
  }
  if (!op && fields.some((f) => f.dlg) && keptOpener && keptOpener.path === path) op = keptOpener;
  const count = fields.length + keptPicks.length + rich.length;
  if (!count) return 0;
  const savedAt = Date.now();
  autoTried.add(savedAt); // saved while logging out: restore only after the NEXT login, not in this page
  write({ userId: String(userId), path, state: routerState(), title: document.title || "", savedAt, reason: reason || "", fields, picks: keptPicks, rich, opener: op });
  return count;
};

/** Ask embedded apps (iframes) to save their own unsaved fields. */
export const notifyFramesToSave = (reason) => {
  document.querySelectorAll("iframe").forEach((f) => {
    try {
      f.contentWindow && f.contentWindow.postMessage({ type: "BU_SAVE_DRAFT", reason }, "*");
    } catch (e) {
      /* frame gone */
    }
  });
};

/**
 * Save here + in iframes, then run `then` (the logout). With an embedded app (HR / Kanban)
 * wait for its answer (it saves its own draft and tells us) — at most 1.5 s — so a slow
 * frame's draft is not lost when the page leaves.
 */
export const saveDraftsThen = (reason, then) => {
  let done = false;
  const go = () => {
    if (done) return;
    done = true;
    window.removeEventListener("message", onReply);
    then();
  };
  const onReply = (e) => {
    const t = e.data && e.data.type;
    if (e.source && e.source !== window && (t === "BU_DRAFT_SAVED" || t === "BU_DRAFT_NONE")) setTimeout(go, 50);
  };
  const hasFrames = !!document.querySelector("iframe");
  if (hasFrames) window.addEventListener("message", onReply);
  try {
    notifyFramesToSave(reason);
    saveDraft(reason);
  } catch (e) {
    /* never block a logout */
  }
  setTimeout(go, hasFrames ? 1500 : 0);
};

// ── Restore ─────────────────────────────────────────────────────────────────────
const setValue = (el, value) => {
  const proto = el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : el.tagName === "SELECT" ? window.HTMLSelectElement.prototype : window.HTMLInputElement.prototype;
  const desc = Object.getOwnPropertyDescriptor(proto, "value");
  if (desc && desc.set) desc.set.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));
};
const fire = (el, type) => el.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window, button: 0 }));
const press = (el) => ["pointerdown", "mousedown", "pointerup", "mouseup", "click"].forEach((t) => fire(el, t));
const waitFor = async (fn, ms) => {
  const end = Date.now() + ms;
  while (Date.now() < end) {
    const v = fn();
    if (v) return v;
    await sleep(150);
  }
  return null;
};

// Text-like fields. `dry`: only count what can be found. Popup fields only inside the open popup.
const restoreFields = (draft, info = {}, dry = false) => {
  const bySig = new Map();
  signatures().forEach((sig, el) => {
    if (!dialogOf(el)) bySig.set(sig, el);
  });
  const dlg = draft.fields.some((f) => f.dlg) ? openDialog() : null;
  const dlgBySig = new Map();
  if (dlg) signatures(dlg).forEach((sig, el) => dlgBySig.set(sig, el));
  let done = 0;
  info.dlg = 0;
  draft.fields.forEach((f) => {
    const el = f.dlg ? dlgBySig.get(f.sig) : bySig.get(f.sig);
    if (el && f.dlg) info.dlg++;
    if (!el || el.disabled || el.readOnly) return;
    if (!dry) {
      if ("checked" in f) {
        if (el.checked !== f.checked) el.click();
      } else if (el.value !== f.value) {
        setValue(el, f.value);
        el.dispatchEvent(new Event("blur", { bubbles: true })); // date pickers commit on blur
      }
      dirty.add(el);
    }
    done++;
  });
  return done;
};

// Dropdowns: open the same dropdown and click the same option again.
const optionWithText = (text) => [...document.querySelectorAll(OPTION_SEL)].find((o) => o.getClientRects().length && textOf(o) === text);
const replayPicks = async (draft) => {
  let n = 0;
  for (const p of draft.picks) {
    const cb = findCombo(p);
    if (!cb) continue;
    const target = cb.matches('[role="combobox"]') ? cb : cb.querySelector('[role="combobox"], input') || cb;
    press(target);
    let opt = await waitFor(() => optionWithText(p.text), 1500);
    if (!opt) {
      // searchable dropdown (react-select / Autocomplete): type the option text to find it
      const inp = target.tagName === "INPUT" ? target : cb.querySelector("input");
      if (inp) {
        inp.focus();
        setValue(inp, p.text);
        opt = await waitFor(() => optionWithText(p.text), 2500);
      }
    }
    if (opt) {
      press(opt);
      n++;
    } else {
      document.activeElement && document.activeElement.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    }
    await sleep(250);
  }
  return n;
};

const restoreRich = (draft) => {
  let n = 0;
  draft.rich.forEach((r) => {
    try {
      if (r.kind === "tiny") {
        const ed = tinyEditors()[r.idx];
        if (!ed) return;
        ed.setContent(r.html);
        if (ed.setDirty) ed.setDirty(true);
        if (ed.fire) ed.fire("change");
        n++;
        return;
      }
      const root = scopeRoot(r.dlg);
      const el = root && editablesIn(root)[r.idx];
      if (!el) return;
      if (el.ckeditorInstance) el.ckeditorInstance.setData(r.html);
      else {
        el.innerHTML = r.html;
        el.dispatchEvent(new Event("input", { bubbles: true }));
      }
      richDirty.add(el);
      n++;
    } catch (e) {
      /* editor not ready */
    }
  });
  return n;
};

const needsPopupOf = (d) => d.fields.some((f) => f.dlg) || d.picks.some((p) => p.dlg) || d.rich.some((r) => r.dlg);
// Is the saved form on screen (and its popup open) so it can be filled in?
const formReady = (draft) => {
  if (needsPopupOf(draft) && !openDialog()) return false;
  if (draft.fields.length) {
    const info = {};
    const n = restoreFields(draft, info, true);
    return draft.fields.some((f) => f.dlg) ? info.dlg > 0 : n > 0;
  }
  if (draft.picks.length) return draft.picks.some((p) => findCombo(p));
  return draft.rich.some((r) => (r.kind === "tiny" ? tinyEditors()[r.idx] : editablesIn(scopeRoot(r.dlg) || document)[r.idx]));
};

// Go to the saved page (and re-open its popup), wait for the form, fill everything in.
// If the form never shows up, a banner offers to try again.
const openAndRestore = (draft) => {
  busy = true;
  hideBanner();
  if (draft.path !== currentPath()) openInApp(draft.path, draft.state);
  const t0 = Date.now();
  let clickedAt = 0;
  const tryNow = async () => {
    // the app may still redirect after logging in (e.g. to its dashboard): go back
    if (draft.path !== currentPath() && Date.now() - t0 < 10000) openInApp(draft.path, draft.state);
    if (draft.path === currentPath()) {
      // popup not open yet: click what opened it (once the list / row has loaded)
      if (needsPopupOf(draft) && !openDialog() && draft.opener && Date.now() - clickedAt > 4000) {
        const el = findOpener(draft.opener);
        if (el) {
          clickedAt = Date.now();
          el.click();
        }
      }
      if (formReady(draft)) {
        await sleep(400); // let the form finish rendering
        const p = await replayPicks(draft); // dropdowns first: picking one may reset fields below it
        const f = restoreFields(draft);
        const r = restoreRich(draft);
        write(null);
        busy = false;
        if (opts.onRestored) opts.onRestored(p + f + r);
        return;
      }
    }
    if (Date.now() - t0 > 30000) {
      busy = false; // give up: the card offers it again
      failedOpen.add(draft.savedAt);
      return;
    }
    setTimeout(tryNow, 500);
  };
  setTimeout(tryNow, 300);
};

// BU: the draft is in an embedded app. Ask its iframe (on the page we just opened) to
// open + fill the saved page; keep asking until it answers (it may still be logging in).
const askFrameToOpen = () => {
  clearInterval(askTimer);
  let tries = 0;
  askTimer = setInterval(() => {
    if (++tries > 60) return clearInterval(askTimer);
    document.querySelectorAll("iframe").forEach((f) => {
      try {
        f.contentWindow && f.contentWindow.postMessage({ type: "BU_OPEN_DRAFT" }, "*");
      } catch (e) {
        /* frame gone */
      }
    });
  }, 700);
};
const openFrameDraft = (fd) => {
  hideBanner();
  if (fd.path !== currentPath()) openInApp(fd.path, null);
  askFrameToOpen();
};

// ── Banner (only when the automatic restore did not work) ───────────────────────
// "/hr" → "HR", "/kanban" → "Kanban"
const pageName = (path) => {
  const seg = String(path || "").split(/[/?]/)[1] || "";
  if (!seg) return "previous";
  return seg.length <= 3 ? seg.toUpperCase() : seg[0].toUpperCase() + seg.slice(1);
};

const hideBanner = () => {
  if (banner) banner.remove();
  banner = null;
};

const showBanner = (draft) => {
  const here = !draft.frame && draft.path === currentPath();
  const failed = failedOpen.has(draft.savedAt);
  const key = `${draft.frame ? "f" : ""}${draft.savedAt}|${here}|${failed}`;
  if (banner && banner.dataset.key === key) return;
  hideBanner();
  const time = new Date(draft.savedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  banner = document.createElement("div");
  banner.dataset.key = key;
  banner.dataset.noDraft = "1";
  banner.setAttribute("role", "status");
  banner.setAttribute(
    "style",
    "position:fixed;left:16px;bottom:16px;z-index:25000;max-width:min(440px,calc(100vw - 32px));background:#fff;color:#1f2937;border:1px solid #e5e7eb;border-left:4px solid #1a7d3e;border-radius:10px;box-shadow:0 8px 28px rgba(0,0,0,.18);padding:14px 16px;font-size:14px;font-family:inherit"
  );
  const btn = "border:0;border-radius:8px;padding:7px 14px;font-size:13px;font-weight:600;cursor:pointer;";
  banner.innerHTML = `
    <div style="font-weight:600;margin-bottom:4px">You have unsaved changes</div>
    <div style="color:#4b5563;margin-bottom:10px">${
      failed
        ? `Saved at ${time} on the ${pageName(draft.path)} page when you were logged out. They could not be filled in — the page or row may have changed.`
        : here
        ? `Saved at ${time} on this page when you were logged out.`
        : `Saved at ${time} on the ${pageName(draft.path)} page when you were logged out.`
    }</div>
    <div style="display:flex;gap:8px;flex-wrap:wrap">
      <button type="button" data-act="main" style="${btn}background:#1a7d3e;color:#fff">${failed ? "Try again" : here ? "Restore" : "Open that page"}</button>
      <button type="button" data-act="discard" style="${btn}background:#eef2f6;color:#1f2937">Discard</button>
    </div>`;
  document.body.appendChild(banner);
  banner.querySelector('[data-act="discard"]').addEventListener("click", () => {
    if (draft.frame) writeFrame(null);
    else write(null);
    hideBanner();
  });
  // Open that page = go to the saved page (and its popup) and fill everything in
  banner.querySelector('[data-act="main"]').addEventListener("click", () => {
    failedOpen.delete(draft.savedAt);
    if (draft.frame) return openFrameDraft(draft);
    openAndRestore(draft);
  });
};

// Another page: what was changed on the previous page is gone. Checked on every edit too
// (not only by the 1-second timer), so typing right after opening a page is never wiped.
const syncPath = () => {
  const path = currentPath();
  if (path === lastPath) return path;
  lastPath = path;
  dirty = new Set();
  dirtyKeys = new Map();
  keptOpener = null;
  picks = [];
  richDirty = new Set();
  return path;
};

const checkBanner = () => {
  const path = syncPath();
  if (busy) return hideBanner();
  const userId = opts.getUserId();
  if (userId) lastUserId = String(userId);
  const draft = read();
  // The card waits for the user: "Open that page" / "Discard". Drafts saved in this page
  // load (we are logging out right now) show only after the next login.
  if (draft && userId && draft.userId === String(userId)) {
    if (autoTried.has(draft.savedAt)) return hideBanner();
    return showBanner(draft);
  }
  const fd = readFrame();
  if (fd && userId && fd.userId === String(userId) && !autoTried.has("f" + fd.savedAt)) {
    // on the page that holds the embedded app, its own card shows instead
    if (fd.path !== path) return showBanner({ ...fd, frame: true });
  }
  hideBanner();
};

/** User id from a stored userData value (object, JSON, or JSON stored twice). */
export const userIdFromStored = (raw) => {
  try {
    let v = raw;
    for (let i = 0; i < 2 && typeof v === "string"; i++) v = JSON.parse(v);
    return (v && (v.id || (v.user && v.user.id))) || null;
  } catch (e) {
    return null;
  }
};

/**
 * Start once per app (embedded apps too: they save and restore their own fields).
 * opts: { getUserId(): string|null, onRestored?(count) }
 */
export const startDraftGuard = (o) => {
  if (started) return;
  started = true;
  opts = { ...o };
  lastPath = currentPath();
  const mine = (e) => e.isTrusted && !(banner && banner.contains(e.target));
  const onEdit = (e) => {
    if (!mine(e)) return;
    syncPath();
    if (isField(e.target)) {
      dirty.add(e.target);
      scheduleKeys();
    }
    const ce = e.target.closest && e.target.closest('[contenteditable="true"]');
    if (ce && !ce.closest("[data-no-draft]")) richDirty.add(ce);
  };
  document.addEventListener(
    "mousedown",
    (e) => {
      if (!mine(e)) return;
      const cb = outerCombo(e.target);
      if (cb) lastCombo = cb;
    },
    true
  );
  document.addEventListener(
    "click",
    (e) => {
      if (!mine(e)) return;
      // an option picked in a dropdown
      syncPath();
      const opt = e.target.closest && e.target.closest(OPTION_SEL);
      if (opt) {
        if (lastCombo && lastCombo.isConnected) {
          try {
            picks.push({ ...comboKey(lastCombo), text: textOf(opt).slice(0, 200) });
            if (picks.length > MAX_PICKS) picks.shift();
          } catch (err) {
            /* ignore */
          }
        }
        return;
      }
      if (dialogOf(e.target)) return; // clicks inside the popup don't change what opened it
      if (!e.target.closest || e.target.closest('.MuiPopover-root, .MuiMenu-root, [role="listbox"], [role="presentation"]')) return; // dropdown menus
      // only things that can open a popup: buttons, links, table rows
      const el = e.target.closest(OPENER_SEL) || e.target.closest('tr, [role="row"]');
      if (!el) return;
      try {
        opener = { ...describeOpener(el), path: currentPath() };
      } catch (err) {
        opener = null;
      }
    },
    true
  );
  // Enter on a highlighted option (keyboard) counts as a pick too
  document.addEventListener(
    "keydown",
    (e) => {
      if (!mine(e) || e.key !== "Enter") return;
      syncPath();
      const cb = outerCombo(e.target);
      const active = cb && document.querySelector('[role="option"][aria-selected="true"], [role="option"].Mui-focused, [class*="option--is-focused"]');
      if (active) {
        picks.push({ ...comboKey(cb), text: textOf(active).slice(0, 200) });
        if (picks.length > MAX_PICKS) picks.shift();
      }
    },
    true
  );
  document.addEventListener("input", onEdit, true);
  document.addEventListener("change", onEdit, true);
  window.addEventListener("message", (e) => {
    const t = e.data && e.data.type;
    if (e.source === window.parent && e.source !== window && t === "BU_OPEN_DRAFT") {
      const uid = opts.getUserId();
      if (!uid) return; // not logged in yet (BU asks again)
      tellParent("BU_DRAFT_OPENED");
      const d = read();
      if (!busy && d && d.userId === String(uid) && !autoTried.has(d.savedAt)) {
        autoTried.add(d.savedAt);
        openAndRestore(d);
      }
      return;
    }
    if (t === "BU_DRAFT_OPENED" && e.source && e.source !== window) {
      clearInterval(askTimer);
      return;
    }
    if (e.source === window.parent && e.source !== window && t === "BU_SAVE_DRAFT") {
      // nothing new typed, but a draft kept earlier (e.g. our own logout): tell BU again
      if (!saveDraft(e.data.reason)) {
        const old = read();
        if (old) tellParent("BU_DRAFT_SAVED", { savedAt: old.savedAt });
        else tellParent("BU_DRAFT_NONE"); // BU need not wait any longer
      }
      return;
    }
    // from one of our iframes (HR / Kanban): remember which page of this app shows it
    if (e.source && e.source !== window && e.source !== window.parent && (t === "BU_DRAFT_SAVED" || t === "BU_DRAFT_CLEARED")) {
      if (t === "BU_DRAFT_CLEARED") return writeFrame(null);
      const userId = opts.getUserId() || lastUserId; // the login may already be cleared (logout in progress)
      if (userId) {
        const savedAt = e.data.savedAt || Date.now();
        autoTried.add("f" + savedAt); // saved while logging out: bring the user back only after the next login
        writeFrame({ userId: String(userId), path: currentPath(), savedAt });
      }
    }
  });
  window.setInterval(checkBanner, 1000);
  checkBanner();
};
